import { findMoveByIdGlobal } from '../data/moves';
import { allSpells, findSpellById } from '../data/spells';
import type { MoveRef, SpellRef } from '../schemas/character';
import type { ClassId } from '../schemas/classId';
import { isAutoSpellGrantLevelRule, type Move, type SpellGrant } from '../schemas/move';
import type { Spell, SpellLevel } from '../schemas/spell';

/**
 * **FI-33 (D2)**: servizio dedicato al pattern «mossa concede N spell da
 * pool filtrato». Modellizzazione completa in
 * `dev-workspace/docs/Fase-D-Level-Up-SSOT.md` §2.2.
 *
 * 4 helper esposti:
 *  - {@link getActiveSpellGrantsForLevelUp}: data una mossa appena presa +
 *    le mosse precedenti del PG, ritorna le `SpellGrant` da applicare in
 *    questo level-up. Filtra per `trigger` (`'on-take'` solo se mossa è
 *    `justAddedMove`, `'on-each-level-up'` per qualsiasi mossa posseduta
 *    al level-up).
 *  - {@link getSpellPoolForGrant}: data una grant + il livello PG +
 *    l'eventuale `ownClassId` (la classe di appartenenza della mossa che
 *    concede la grant), ritorna gli spell selezionabili. Applica filtri
 *    `source`, `levelRule`, esclusione spell già posseduti.
 *  - {@link isGrantSelectionValid}: verifica che la scelta dello user per
 *    una specifica grant rispetti `count` e `perLevelCap`.
 *  - {@link buildAllSpellRefs}: data la mappa `grantIndex → SpellRef[]`
 *    raccolta dalla UI, produce l'array piatto da passare a `applyLevelUp`.
 */

/**
 * Una grant attiva in un contesto di level-up, con riferimento alla
 * mossa-source e all'indice della grant nell'array `grantsSpells` della
 * mossa (per chiavi UI deterministiche).
 */
export interface ActiveSpellGrant {
  /** ID della mossa che concede la grant. */
  moveId: string;
  /** Indice della grant nell'array `grantsSpells` (0-based). */
  grantIndex: number;
  /** La grant stessa. */
  grant: SpellGrant;
}

/**
 * Ritorna l'array di `ActiveSpellGrant` da applicare durante un level-up.
 *
 * Logica:
 *  - Per le mosse già possedute (`character.moves`): considera solo le
 *    grant con `trigger: 'on-each-level-up'` (es. Corpus Arcano del Mago:
 *    «+1 spell ogni level-up» finché possiedi la mossa).
 *  - Per la mossa appena scelta (`justAddedMove`): considera **solo** le
 *    grant con `trigger: 'on-take'`. Le grant `'on-each-level-up'` su una
 *    mossa appena acquisita NON sono attive al level-up corrente — partono
 *    dal level-up successivo (quando la mossa è in `character.moves`
 *    PRE level-up). **FI-55**: pattern coerente con la regola d'uso
 *    «se la mossa è appena stata ottenuta in bundle, l'unica scelta sono
 *    le mosse base»; evita doppia spell pick al primo level-up della mossa
 *    (es. PG Guerriero L1→L2 sceglie *Lanciare Incantesimi Arcani* multiclasse
 *    → bundle *Corpus Arcano* NON deve aggiungere «+1 spell L1» al LU corrente).
 *
 * **Stesso ID mossa due volte (caso multiclasse all-inclusive bundle)**:
 * non gestito qui (le mosse-bundle sono di scope D7/FI-17). In D2 ogni
 * mossa appare al massimo 1 volta.
 *
 * @param classId classe del PG (per il lookup `findMoveByIdAnyScope`)
 * @param previousMoves mosse già in `Character.moves` PRIMA del level-up
 * @param justAddedMove mossa scelta in questo level-up (può anche essere
 *   undefined nel flow se l'utente non ha ancora scelto, in tal caso si
 *   considerano solo le `'on-each-level-up'` delle mosse precedenti)
 */
export function getActiveSpellGrantsForLevelUp(
  classId: ClassId,
  previousMoves: readonly MoveRef[],
  justAddedMove: MoveRef | undefined,
): ActiveSpellGrant[] {
  return getActiveSpellGrantsForLevelUpMany(
    classId,
    previousMoves,
    justAddedMove ? [justAddedMove] : [],
  );
}

/**
 * **FI-35 (D-FI35)** — variante array di {@link getActiveSpellGrantsForLevelUp}.
 *
 * Accetta una lista di mosse appena acquisite in questo level-up (main +
 * granted from-group + futuro bundle), non solo una singola. Gap D di
 * FI-35 era proprio che la sotto-scelta granted non veniva considerata.
 *
 * **Filtro auto/pick** (FI-35): ritorna **solo** le grant di famiglia
 * "pick" che richiedono input UI dal giocatore. Le grant "auto"
 * (`all-of-level`, `fixed-list`) sono applicate dal service
 * `applyMoveAcquisition` direttamente e NON devono apparire nella UI di
 * selezione.
 */
export function getActiveSpellGrantsForLevelUpMany(
  classId: ClassId,
  previousMoves: readonly MoveRef[],
  justAddedMoves: readonly MoveRef[],
): ActiveSpellGrant[] {
  const result: ActiveSpellGrant[] = [];

  // **D7-bundle**: lookup `findMoveByIdGlobal` (cross-class) per
  // includere le grant delle mosse acquisite via multiclasse o bundle
  // (es. PG Guerriero che pesca `mago.lanciare-incantesimi-arcani` via
  // multiclasse riceve in bundle `mago.corpus-arcano` con grant
  // `on-each-level-up`). Pre-D7-bundle si usava `findMoveByIdAnyScope`
  // che non trovava mosse cross-class. Il `classId` resta per coerenza
  // di chiamata; il `sourceClassId` della grant viene risolto a runtime
  // dal caller `getSpellPoolForGrant`.
  for (const ref of previousMoves) {
    const move = findMoveByIdGlobal(ref.id);
    if (!move?.grantsSpells) continue;
    appendGrants(
      result,
      move,
      (g) => g.trigger === 'on-each-level-up' && !isAutoSpellGrantLevelRule(g.levelRule),
    );
  }

  for (const ref of justAddedMoves) {
    const move = findMoveByIdGlobal(ref.id);
    if (!move?.grantsSpells) continue;
    // **FI-55**: solo grant `on-take` su mosse appena acquisite. Le grant
    // `on-each-level-up` partono dal level-up SUCCESSIVO (la mossa sarà
    // allora in `previousMoves`). Evita doppia spell pick al primo LU della
    // mossa via multiclass bundle (es. Corpus Arcano da bundle LI Arcani
    // NON deve aggiungere «+1 spell L1» al LU corrente).
    appendGrants(
      result,
      move,
      (g) => g.trigger === 'on-take' && !isAutoSpellGrantLevelRule(g.levelRule),
    );
  }

  // Riferimento parametro `classId` mantenuto per coerenza della firma
  // e per consumer downstream che potrebbero filtrare per classe del PG.
  void classId;
  return result;
}

/**
 * **FI-36 (PR-A2 Step 5b)** — Variante "display" che NON filtra le grant
 * di famiglia "auto" (`all-of-level`/`fixed-list`). Usato dalla UI builder L1
 * (`SectionSpellSelection`) per mostrare anche le spell auto-aggiunte
 * (trucchetti Mago, L0+L1 tradizione Chierico/Druido/Paladino, Suppliche
 * Warlock) in modalità read-only. Le grant auto verranno comunque applicate
 * al finalize via `applyMoveAcquisition` — questo helper serve solo per il
 * rendering.
 *
 * Differenza vs {@link getActiveSpellGrantsForLevelUpMany}: ritorna SIA pick
 * SIA auto. Il consumer discrimina via {@link isAutoSpellGrantLevelRule}
 * per decidere se renderizzare checkbox o lista read-only.
 */
export function getDisplayableSpellGrantsForLevelUpMany(
  classId: ClassId,
  previousMoves: readonly MoveRef[],
  justAddedMoves: readonly MoveRef[],
): ActiveSpellGrant[] {
  const result: ActiveSpellGrant[] = [];

  for (const ref of previousMoves) {
    const move = findMoveByIdGlobal(ref.id);
    if (!move?.grantsSpells) continue;
    appendGrants(result, move, (g) => g.trigger === 'on-each-level-up');
  }

  for (const ref of justAddedMoves) {
    const move = findMoveByIdGlobal(ref.id);
    if (!move?.grantsSpells) continue;
    appendGrants(result, move, (g) => g.trigger === 'on-take');
  }

  void classId;
  return result;
}

/**
 * Calcola il pool di spell selezionabili per una specifica grant, dato lo
 * stato corrente del PG.
 *
 * Filtri applicati (in ordine):
 *  1. **`source`**: `own-class` → spell con `availableTo.some(a => a.classId
 *     === ownClassId)`; `other-classes` → escludi la propria; `any-class` →
 *     tutto il catalogo.
 *  2. **`levelRule`**: `fixed` → `spell.level === level`;
 *     `lte-character-level` → `spell.level <= characterLevel`;
 *     `any-with-cap` → `spell.level <= maxLevel ?? 9`.
 *  3. **Esclusione spell già posseduti**: filtra fuori gli ID già in
 *     `alreadyKnownSpellIds`.
 *
 * **Coerenza tra `ownClassId` e `source`**: per `'other-classes'` /
 * `'any-class'`, `ownClassId` serve a sapere quale classe escludere (per
 * `'other-classes'`) o per coerenza semantica. Per `'own-class'` la
 * classe è quella della mossa che concede la grant — qui passata
 * esplicitamente per evitare lookup inverso `moveId → classId`.
 */
export function getSpellPoolForGrant(
  grant: SpellGrant,
  characterLevel: number,
  ownClassId: ClassId,
  alreadyKnownSpellIds: ReadonlySet<string>,
): readonly Spell[] {
  return allSpells.filter(
    (spell) =>
      matchesSource(spell, grant.source.kind, ownClassId) &&
      matchesLevelRule(spell, grant.levelRule, characterLevel) &&
      !alreadyKnownSpellIds.has(spell.id),
  );
}

/**
 * Verifica che le scelte fatte dal giocatore per una specifica grant
 * rispettino `count` e `perLevelCap`. Ritorna `true` se valide e complete.
 *
 * Logica:
 *  - `selected.length === grant.count` (non meno, non più)
 *  - Ogni `spellId` selezionato esiste nel catalogo (`findSpellById`)
 *  - Se `grant.perLevelCap` è definito, per ogni `(spellLevel, max)` il
 *    numero di spell scelti con `spell.level === level` non supera `max`
 */
export function isGrantSelectionValid(
  grant: SpellGrant,
  selectedSpellIds: readonly string[],
): boolean {
  // FI-35: per grant "auto" (no count) non c'è selezione da validare.
  if (grant.count === undefined) return true;
  if (selectedSpellIds.length !== grant.count) return false;
  const spells = selectedSpellIds.map(findSpellById);
  if (spells.some((s) => s === undefined)) return false;
  if (!grant.perLevelCap) return true;
  return checkPerLevelCap(spells as readonly Spell[], grant.perLevelCap);
}

/**
 * Predicate: tutte le grant attive hanno selezioni complete e valide?
 * Usato dal `LevelUpStepSpells` UI per abilitare il bottone «Avanti».
 */
export function areAllSpellGrantsSelectionsValid(
  grants: readonly ActiveSpellGrant[],
  selectionsByGrantKey: Readonly<Record<string, readonly string[]>>,
): boolean {
  return grants.every((ag) => {
    const key = getGrantKey(ag);
    const sel = selectionsByGrantKey[key] ?? [];
    return isGrantSelectionValid(ag.grant, sel);
  });
}

/**
 * Aggregatore: data la mappa `grantKey → spellIds` (raccolta dalla UI),
 * produce un array piatto di `SpellRef` da passare a `applyLevelUp` come
 * `LevelUpInput.spellsAdded`. Ordine deterministico (segue `grants`).
 */
export function buildAllSpellRefs(
  grants: readonly ActiveSpellGrant[],
  selectionsByGrantKey: Readonly<Record<string, readonly string[]>>,
): SpellRef[] {
  const result: SpellRef[] = [];
  const seen = new Set<string>();
  for (const ag of grants) {
    const key = getGrantKey(ag);
    const ids = selectionsByGrantKey[key] ?? [];
    for (const id of ids) {
      if (!seen.has(id)) {
        seen.add(id);
        result.push({ id });
      }
    }
  }
  return result;
}

/**
 * Chiave deterministica `moveId#grantIndex` per identificare una grant
 * nella mappa selezioni della UI. Esposta per coerenza UI ↔ service.
 */
export function getGrantKey(ag: ActiveSpellGrant): string {
  return `${ag.moveId}#${ag.grantIndex}`;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function appendGrants(
  acc: ActiveSpellGrant[],
  move: Move,
  predicate: (g: SpellGrant) => boolean,
): void {
  if (!move.grantsSpells) return;
  move.grantsSpells.forEach((grant, grantIndex) => {
    if (predicate(grant)) acc.push({ moveId: move.id, grantIndex, grant });
  });
}

function matchesSource(
  spell: Spell,
  sourceKind: SpellGrant['source']['kind'],
  ownClassId: ClassId,
): boolean {
  if (sourceKind === 'any-class') return true;
  // `SpellAccess.classId` è ristretto alle 5 casteggianti DWR, mentre
  // `ClassId` copre tutte 12. Cast a string per consentire il confronto
  // di equality con classi non-casteggianti (runtime: non matchano mai).
  const classesAccessing: readonly string[] = spell.availableTo.map((a) => a.classId);
  if (sourceKind === 'own-class') return classesAccessing.includes(ownClassId);
  // sourceKind === 'other-classes'
  return classesAccessing.some((c) => c !== ownClassId);
}

function matchesLevelRule(
  spell: Spell,
  levelRule: SpellGrant['levelRule'],
  characterLevel: number,
): boolean {
  switch (levelRule.kind) {
    case 'fixed':
      return spell.level === levelRule.level;
    case 'lte-character-level':
      return spell.level <= characterLevel;
    case 'any-with-cap': {
      const cap = levelRule.maxLevel ?? 9;
      return spell.level <= cap;
    }
    // FI-35: famiglia "auto". Queste regole non passano normalmente per
    // `getSpellPoolForGrant` (le grant auto sono filtrate via
    // `getActiveSpellGrantsForLevelUp`), ma supportiamo il match per
    // coerenza e type-safety.
    case 'all-of-level':
      return spell.level === levelRule.level;
    case 'fixed-list':
      return levelRule.spellIds.includes(spell.id);
  }
}

function checkPerLevelCap(
  spells: readonly Spell[],
  perLevelCap: Readonly<Record<string, number>>,
): boolean {
  const counts = new Map<SpellLevel, number>();
  for (const s of spells) {
    counts.set(s.level, (counts.get(s.level) ?? 0) + 1);
  }
  for (const [levelStr, max] of Object.entries(perLevelCap)) {
    const level = Number(levelStr) as SpellLevel;
    if ((counts.get(level) ?? 0) > max) return false;
  }
  return true;
}
